import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './SmartSearch.module.css';

// Remove acentos para busca fuzzy
const removeAccents = (str) => {
    return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
};

// Destaca a correspondência no texto
const highlightMatch = (text, query) => {
    if (!query || !text) return text;

    const normalizedText = removeAccents(text.toLowerCase());
    const normalizedQuery = removeAccents(query.toLowerCase());
    const index = normalizedText.indexOf(normalizedQuery);

    if (index === -1) return text;

    const before = text.substring(0, index);
    const match = text.substring(index, index + query.length);
    const after = text.substring(index + query.length);

    return (
        <>
            {before}
            <span className={styles.highlight}>{match}</span>
            {after}
        </>
    );
};

const SmartSearch = ({
    onSearch,
    onSelect,
    placeholder = 'Buscar produtos...',
    searchEndpoint = '/api/search/suggestions',
    storageKey = 'smartSearchHistory',
    maxHistory = 5,
    debounceMs = 300,
    showSuggestions = true,
    minChars = 2
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [focusedSection, setFocusedSection] = useState('suggestions'); // 'suggestions' | 'recent'
    const inputRef = useRef(null);
    const containerRef = useRef(null);
    const debounceRef = useRef(null);

    // Carregar histórico do localStorage
    useEffect(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                setRecentSearches(JSON.parse(saved));
            }
        } catch (e) {
            console.warn('Erro ao carregar histórico de busca:', e);
        }
    }, [storageKey]);

    // Salvar no histórico
    const saveToHistory = useCallback((term) => {
        if (!term || term.length < minChars) return;

        setRecentSearches(prev => {
            const filtered = prev.filter(t => t.toLowerCase() !== term.toLowerCase());
            const updated = [term, ...filtered].slice(0, maxHistory);
            localStorage.setItem(storageKey, JSON.stringify(updated));
            return updated;
        });
    }, [storageKey, maxHistory, minChars]);

    // Buscar sugestões
    const fetchSuggestions = useCallback(async (query) => {
        if (!query || query.length < minChars || !showSuggestions) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${searchEndpoint}?q=${encodeURIComponent(query)}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSuggestions(data.suggestions || []);
            }
        } catch (error) {
            console.error('Erro ao buscar sugestões:', error);
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [searchEndpoint, minChars, showSuggestions]);

    // Debounce da busca
    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
            if (searchTerm) {
                fetchSuggestions(searchTerm);
            } else {
                setSuggestions([]);
            }
        }, debounceMs);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [searchTerm, fetchSuggestions, debounceMs]);

    // Fechar ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Executar busca
    const handleSearch = useCallback((term) => {
        saveToHistory(term);
        setIsOpen(false);
        if (onSearch) onSearch(term);
    }, [onSearch, saveToHistory]);

    // Selecionar item
    const handleSelectItem = useCallback((item) => {
        setSearchTerm(item.descricao || item.nome || item);
        saveToHistory(item.descricao || item.nome || item);
        setIsOpen(false);
        if (onSelect) onSelect(item);
        if (onSearch) onSearch(item.descricao || item.nome || item);
    }, [onSelect, onSearch, saveToHistory]);

    // Navegação por teclado
    const handleKeyDown = (e) => {
        const totalItems = suggestions.length + recentSearches.length;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1) % totalItems);
            setIsOpen(true);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev - 1 + totalItems) % totalItems);
            setIsOpen(true);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
                handleSelectItem(suggestions[selectedIndex]);
            } else if (selectedIndex >= suggestions.length && selectedIndex < totalItems) {
                const recentIndex = selectedIndex - suggestions.length;
                handleSearch(recentSearches[recentIndex]);
            } else {
                handleSearch(searchTerm);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
            setSelectedIndex(-1);
        }
    };

    // Limpar histórico
    const handleClearHistory = () => {
        localStorage.removeItem(storageKey);
        setRecentSearches([]);
    };

    // Remover item do histórico
    const handleRemoveFromHistory = (e, term) => {
        e.stopPropagation();
        setRecentSearches(prev => {
            const updated = prev.filter(t => t !== term);
            localStorage.setItem(storageKey, JSON.stringify(updated));
            return updated;
        });
    };

    const hasContent = suggestions.length > 0 || recentSearches.length > 0;

    return (
        <div className={styles.smartSearch} ref={containerRef}>
            <div className={styles.inputWrapper}>
                <svg className={styles.searchIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>

                <input
                    ref={inputRef}
                    type="text"
                    className={styles.input}
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setIsOpen(true);
                        setSelectedIndex(-1);
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={handleKeyDown}
                />

                {loading && (
                    <div className={styles.spinner}>
                        <div className={styles.spinnerInner}></div>
                    </div>
                )}

                {searchTerm && !loading && (
                    <button
                        type="button"
                        className={styles.clearButton}
                        onClick={() => {
                            setSearchTerm('');
                            setSuggestions([]);
                            // Notificar o componente pai que a busca foi limpa
                            if (onSearch) onSearch('');
                            inputRef.current?.focus();
                        }}
                    >
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {isOpen && hasContent && (
                <div className={styles.dropdown}>
                    {/* Sugestões de produtos */}
                    {suggestions.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <span>📦 Produtos encontrados</span>
                                <span className={styles.badge}>{suggestions.length}</span>
                            </div>
                            <ul className={styles.list}>
                                {suggestions.map((item, index) => (
                                    <li
                                        key={item.codigo || index}
                                        className={`${styles.item} ${selectedIndex === index ? styles.selected : ''}`}
                                        onClick={() => handleSelectItem(item)}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                    >
                                        {item.imagem && (
                                            <img
                                                src={item.imagem}
                                                alt=""
                                                className={styles.itemImage}
                                                onError={(e) => e.target.style.display = 'none'}
                                            />
                                        )}
                                        <div className={styles.itemContent}>
                                            <div className={styles.itemName}>
                                                {highlightMatch(item.descricao || item.nome || '', searchTerm)}
                                            </div>
                                            <div className={styles.itemMeta}>
                                                {item.codigo && <span className={styles.itemCode}>#{item.codigo}</span>}
                                                {item.fornecedor && <span className={styles.itemFornecedor}>{item.fornecedor}</span>}
                                                {item.preco && <span className={styles.itemPreco}>R$ {item.preco.toFixed(2)}</span>}
                                            </div>
                                        </div>
                                        {item.estoque !== undefined && (
                                            <span className={`${styles.itemEstoque} ${item.estoque > 0 ? styles.emEstoque : styles.semEstoque}`}>
                                                {item.estoque > 0 ? `${item.estoque} un` : 'Sem estoque'}
                                            </span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Buscas recentes */}
                    {recentSearches.length > 0 && (
                        <div className={styles.section}>
                            <div className={styles.sectionHeader}>
                                <span>🕐 Buscas recentes</span>
                                <button
                                    className={styles.clearHistory}
                                    onClick={handleClearHistory}
                                >
                                    Limpar
                                </button>
                            </div>
                            <ul className={styles.list}>
                                {recentSearches.map((term, index) => (
                                    <li
                                        key={term}
                                        className={`${styles.item} ${styles.recentItem} ${selectedIndex === suggestions.length + index ? styles.selected : ''}`}
                                        onClick={() => handleSearch(term)}
                                        onMouseEnter={() => setSelectedIndex(suggestions.length + index)}
                                    >
                                        <svg className={styles.historyIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className={styles.recentText}>{term}</span>
                                        <button
                                            className={styles.removeRecent}
                                            onClick={(e) => handleRemoveFromHistory(e, term)}
                                        >
                                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Dica de busca */}
                    {searchTerm.length > 0 && searchTerm.length < minChars && (
                        <div className={styles.hint}>
                            Digite pelo menos {minChars} caracteres para buscar
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SmartSearch;
